import React from "react";
import { format } from "date-fns";
import { AuditLog } from "../types";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  RiLoginBoxLine,
  RiLogoutBoxLine,
  RiAddCircleLine,
  RiEdit2Line,
  RiDeleteBin6Line,
  RiEyeLine,
  RiDownload2Line,
  RiFileList3Line,
  RiUpload2Line,
} from "react-icons/ri";

interface AuditTableProps {
  auditLogs: AuditLog[];
  isLoading: boolean;
}

export const AuditTable: React.FC<AuditTableProps> = ({
  auditLogs,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-t-2 border-primary rounded-full"></div>
      </div>
    );
  }

  if (auditLogs.length === 0) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium">No audit logs found</h3>
          <p className="text-muted-foreground">
            Try adjusting your filters or search criteria
          </p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[60vh] rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[160px]">Timestamp</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Resource</TableHead>
            <TableHead className="w-[150px]">IP Address</TableHead>
            <TableHead className="text-right">Details</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {auditLogs.map((log) => (
            <TableRow key={log.auditId}>
              <TableCell className="font-mono text-xs">
                {format(new Date(log.timestamp), "yyyy-MM-dd HH:mm:ss")}
              </TableCell>
              <TableCell>
                <div className="font-medium">{log.userName}</div>
                <div className="text-xs text-muted-foreground">
                  {log.userEmail}
                </div>
              </TableCell>
              <TableCell>
                <ActionBadge action={log.action} />
              </TableCell>
              <TableCell>
                <div className="font-medium">
                  {formatResourceType(log.resourceType)}
                </div>
                {log.resourceName && (
                  <div className="text-xs text-muted-foreground">
                    {log.resourceName}
                  </div>
                )}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {log.ipAddress}
              </TableCell>
              <TableCell className="text-right">
                {log.details ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="text-sm text-muted-foreground hover:text-foreground cursor-help underline underline-offset-4">
                          View details
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="max-w-xs">{log.details}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ScrollArea>
  );
};

// Helper components
const ActionBadge: React.FC<{ action: string }> = ({ action }) => {
  const config = getActionConfig(action);

  return (
    <Badge variant={config.variant} className="flex items-center gap-1">
      {config.icon}
      <span>{formatActionType(action)}</span>
    </Badge>
  );
};

// Helper functions
function formatActionType(action: string): string {
  return action.charAt(0).toUpperCase() + action.slice(1);
}

function formatResourceType(resourceType: string): string {
  return resourceType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getActionConfig(action: string) {
  switch (action) {
    case "login":
      return {
        icon: <RiLoginBoxLine className="h-3 w-3" />,
        variant: "outline" as const,
      };
    case "logout":
      return {
        icon: <RiLogoutBoxLine className="h-3 w-3" />,
        variant: "outline" as const,
      };
    case "create":
      return {
        icon: <RiAddCircleLine className="h-3 w-3" />,
        variant: "default" as const,
      };
    case "update":
      return {
        icon: <RiEdit2Line className="h-3 w-3" />,
        variant: "secondary" as const,
      };
    case "delete":
      return {
        icon: <RiDeleteBin6Line className="h-3 w-3" />,
        variant: "destructive" as const,
      };
    case "view":
      return {
        icon: <RiEyeLine className="h-3 w-3" />,
        variant: "outline" as const,
      };
    case "download":
      return {
        icon: <RiDownload2Line className="h-3 w-3" />,
        variant: "secondary" as const,
      };
    case "generate":
      return {
        icon: <RiFileList3Line className="h-3 w-3" />,
        variant: "default" as const,
      };
    case "upload":
      return {
        icon: <RiUpload2Line className="h-3 w-3" />,
        variant: "secondary" as const,
      };
    default:
      return {
        icon: null,
        variant: "outline" as const,
      };
  }
}
